import { ReactElement } from "react";
import { t } from "@lingui/core/macro";

import { Banner } from "_/components/banner";
import { Button } from "_/components/button";
import { Skeleton } from "_/components/skeleton";
import { AdminView } from "_/components/admin-view";
import { MachineAuthorize } from "_/components/machine-authorize";
import { DocsView } from "_/components/docs-view";
import { HomeDashboard } from "_/components/home-dashboard";
import { MachinesView } from "_/components/machines-view";
import { ProjectsView } from "_/components/projects-view";
import { InternalView } from "_/components/internal-view";
import { Route, Redirect, useLocation } from "_/components/router";
import { SettingsView } from "_/components/settings-view";
import { Sidebar } from "_/components/sidebar";
import { useAddToast } from "_/components/toasts";
import { JobsView } from "_/components/jobs";

// Alias routes to rt for formatting prettiness.
import { routes as rt, routeUrls, isOrgUrl, matchesAnyRoute } from "_/routes";

import {
  useCheckCurrentOrg,
  useOrgs,
  useCheckAdminAccess,
  useIsDocsOnly,
} from "_/data/orgs";
import { useMailEnabled } from "_/data/server";
import { useCheckUser, useSendVerificationEmail } from "_/data/users";
import { useCheckRootPermissions } from "_/data/rbac";

import * as S from "./styled";

// Component to intercept loading of `/admin` routes if the user does not have access
function AdminViewPreload() {
  switch (useCheckAdminAccess()) {
    case "yes":
      return <AdminView />;
    case "no":
      return <Redirect to={rt.index} />;
    case "loading":
    default:
      return <></>;
  }
}

// Routes that docs_user role members are allowed to access.
// All other routes redirect to /docs.
const DOCS_USER_ALLOWED_ROUTES: Array<string | RegExp> = [
  rt.docs.wildcards.index,
  rt.settings.wildcards.index,
  rt.authorize,
];

function DocsUserRedirect() {
  const isDocsUser = useIsDocsOnly();
  const [location] = useLocation();

  if (!isDocsUser) return null;

  const isAllowed = matchesAnyRoute(location, DOCS_USER_ALLOWED_ROUTES);

  return isAllowed ? null : <Redirect to={routeUrls.docs.index} />;
}

export const Private = (): ReactElement => {
  const addToast = useAddToast();
  const { data: user } = useCheckUser();
  const { data: orgs } = useOrgs();
  const { data: rootPermissions } = useCheckRootPermissions();
  const { data: currentOrg } = useCheckCurrentOrg();
  const [location, _setLocation] = useLocation();
  const mailEnabled = useMailEnabled();
  const {
    mutateAsync: sendVerificationEmail,
    isPending: isSendingVerificationEmail,
  } = useSendVerificationEmail();

  async function resendVerificationEmail() {
    try {
      await sendVerificationEmail();
      addToast({
        kind: "success",
        title: t`components.email-verification-banner.sent-title`,
        content: t`components.email-verification-banner.sent-content`,
      });
    } catch {
      addToast({
        kind: "danger",
        title: t`components.email-verification-banner.error-title`,
        content: t`components.email-verification-banner.error-content`,
      });
    }
  }

  // Ensure that the user, orgs, root domain permissions, and current org query have all resolved
  // before navigating to private routes. By doing so here, we can safely use the `useCurrentUser`,
  // `useOrgs` and `useCurrentOrg` hooks on any private page without having to handle the
  // `undefined` case, as well as ensure that the user has been checked for root domain permissions
  // before navigating to any admin routes. `currentOrg` is `undefined` while loading and `null`
  // when the user has no current org set; only the `undefined` case blocks here so the no-orgs
  // redirect below can still fire.
  if (!user || !orgs || !rootPermissions || currentOrg === undefined) {
    return (
      <S.Wrapper>
        <S.MainArea>
          <Skeleton variant="block" />
        </S.MainArea>
      </S.Wrapper>
    );
  }

  if (!currentOrg && !orgs.length && isOrgUrl(location)) {
    return <Redirect to={routeUrls.settings.index} />;
  }

  // Verification emails cannot be sent on installs without email.
  const resendVerificationButton = mailEnabled !== false && (
    <Button
      kind="secondary"
      size="small"
      disabled={isSendingVerificationEmail}
      onClick={() => void resendVerificationEmail()}
    >
      {t`components.email-verification-banner.resend`}
    </Button>
  );

  return (
    <S.Wrapper>
      <Sidebar />
      <S.MainArea>
        {!user.verified && (
          <Banner
            kind="warning"
            role="alert"
            title={t`components.email-verification-banner.title`}
            actions={resendVerificationButton}
          >
            {t`components.email-verification-banner.content`}
          </Banner>
        )}
        <DocsUserRedirect />
        <Route path={rt.index} component={HomeDashboard} />
        <Route path={rt.jobs} component={JobsView} />
        <Route path={rt.projects.wildcards.index} component={ProjectsView} />
        <Route path={rt.settings.wildcards.index} component={SettingsView} />
        <Route path={rt.machines.wildcards.index} component={MachinesView} />
        <Route path={rt.admin.wildcards.index} component={AdminViewPreload} />
        <Route path={rt.internal.wildcards.index} component={InternalView} />
        <Route path={rt.authorize} component={MachineAuthorize} />
        <Route path={rt.docs.wildcards.index} component={DocsView} />
      </S.MainArea>
    </S.Wrapper>
  );
};
